import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { listSignalRules } from "../lib/api-modules/signal-rules.js";
import { useAuth } from "../lib/auth-context.js";

/**
 * Phase 13.5 — Alerts discoverability badge.
 *
 * IA review (DECISIONS.md 2026-05-09 IA review block) moved Alerts out
 * of the primary sidebar into the footer-anchored Settings tray. To
 * preserve discoverability the IA called for "a persistent badge in
 * the topbar (count of unread/urgent alerts)".
 *
 * v1 scope: shows the count of ARMED signal rules as a proxy for
 * "things being watched on your behalf". A genuine unread/urgent count
 * requires an alerts-fired inbox surface (per-firing read/unread
 * state, new API route, schema work) that doesn't exist today —
 * deferred to Phase 17+ ops-hardening per the rationale in
 * DECISIONS.md.
 *
 * Click navigates to /alerts.
 */
export function AlertsBadge() {
  const { user } = useAuth();
  const rulesQuery = useQuery({
    queryKey: ["signal-rules"] as const,
    queryFn: listSignalRules,
    enabled: !!user,
    staleTime: 60_000,
  });
  const armedCount =
    rulesQuery.data?.signal_rules.filter(
      (r) => r.is_armed === true && r.is_archived !== true,
    ).length ?? 0;

  // Hide entirely while loading or when there are no armed rules —
  // empty badges are noise.
  if (rulesQuery.isLoading || rulesQuery.isError || armedCount === 0) {
    return null;
  }

  return (
    <Link
      to="/alerts"
      title={`${armedCount} armed alert${armedCount === 1 ? "" : "s"}`}
      aria-label={`Alerts: ${armedCount} armed`}
      className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border-default)] px-2 py-1 text-xs font-medium text-[var(--color-muted)] hover:bg-[var(--color-elevated)] hover:text-[var(--color-accent)]"
    >
      <BellIcon />
      <span className="tabular-nums">{armedCount}</span>
    </Link>
  );
}

function BellIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 8a6 6 0 1 1 12 0c0 5 1 6 2 7H4c1-1 2-2 2-7Z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </svg>
  );
}
